/**
 * LayoutTree - Manages the hierarchy of LayoutNodes
 *
 * CRITICAL: This is pure data manipulation. No Babylon.js imports.
 */

import type { LayoutNode, Vector3D } from './types';
import { createLayoutNode, createLayoutContainer, createLayoutItem, createVector3D } from './types';

export class LayoutTree {
  /** All nodes indexed by ID */
  private nodes: Map<string, LayoutNode> = new Map();

  /** Root node IDs (nodes with no parent) */
  private rootIds: Set<string> = new Set();

  // === BASIC OPERATIONS ===

  /**
   * Add a node to the tree.
   * If parentId is specified, the node becomes a child of that parent.
   */
  addNode(node: LayoutNode, parentId?: string): void {
    this.nodes.set(node.id, node);

    if (parentId) {
      const parent = this.nodes.get(parentId);
      if (parent) {
        node.parentId = parentId;
        if (!parent.childrenIds.includes(node.id)) {
          parent.childrenIds.push(node.id);
        }

        // A child of an auto-layout container needs an item to be flowed. It
        // gets the defaults and nothing more: cross-axis alignment is left
        // UNSET so the engine's `item?.alignSelfCross ?? container.alignCrossA`
        // does the inheriting. Seeding the container's value here produced the
        // same result — every read site already falls back — while making an
        // inherited value indistinguishable from one the author chose, which
        // matters now that those fields can be authored at all.
        if (parent.layoutContainer && !node.layoutItem) {
          node.layoutItem = createLayoutItem();
        }
      } else {
        // Caller is responsible for correct tree construction; this is a defensive fallback.
        // Using console.warn since LayoutTree has zero external dependencies (pure data module).
        node.parentId = null;
        this.rootIds.add(node.id);
      }
    } else {
      node.parentId = null;
      this.rootIds.add(node.id);
    }
  }

  /**
   * Remove a node from the tree.
   * Children are reparented to the removed node's parent (or become roots).
   */
  removeNode(nodeId: string): LayoutNode | null {
    const node = this.nodes.get(nodeId);
    if (!node) return null;

    // Reparent children
    for (const childId of node.childrenIds) {
      const child = this.nodes.get(childId);
      if (child) {
        child.parentId = node.parentId;
        if (node.parentId) {
          const grandparent = this.nodes.get(node.parentId);
          if (grandparent && !grandparent.childrenIds.includes(childId)) {
            grandparent.childrenIds.push(childId);
          }
        } else {
          this.rootIds.add(childId);
        }
      }
    }

    // Remove from parent's children list
    if (node.parentId) {
      const parent = this.nodes.get(node.parentId);
      if (parent) {
        parent.childrenIds = parent.childrenIds.filter(id => id !== nodeId);
      }
    } else {
      this.rootIds.delete(nodeId);
    }

    this.nodes.delete(nodeId);
    return node;
  }

  /** Get a node by ID. */
  getNode(nodeId: string): LayoutNode | undefined {
    return this.nodes.get(nodeId);
  }

  /** Get all root nodes (nodes with no parent). */
  getRoots(): LayoutNode[] {
    return Array.from(this.rootIds)
      .map(id => this.nodes.get(id))
      .filter((n): n is LayoutNode => n !== undefined);
  }

  /** Get children of a node. */
  getChildren(nodeId: string): LayoutNode[] {
    const node = this.nodes.get(nodeId);
    if (!node) return [];
    return node.childrenIds
      .map(id => this.nodes.get(id))
      .filter((n): n is LayoutNode => n !== undefined);
  }

  /** Get all nodes. */
  getAllNodes(): LayoutNode[] {
    return Array.from(this.nodes.values());
  }

  // === REPARENTING ===

  /** Move a node to a new parent. */
  reparent(nodeId: string, newParentId: string | null): void {
    this.reparentAt(nodeId, newParentId, null);
  }

  /** Move a node to a new parent at a specific index. */
  reparentAt(nodeId: string, newParentId: string | null, insertIndex: number | null): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;

    const oldParentId = node.parentId;

    // Prevent circular reference
    if (newParentId && newParentId !== oldParentId && this.isDescendant(newParentId, nodeId)) {
      return;
    }

    // Adjust insert index for same-parent reordering
    let adjustedInsertIndex = insertIndex;
    if (oldParentId === newParentId && insertIndex != null) {
      if (oldParentId) {
        const parent = this.nodes.get(oldParentId);
        if (parent) {
          const oldIndex = parent.childrenIds.indexOf(nodeId);
          if (oldIndex >= 0 && insertIndex > oldIndex) {
            adjustedInsertIndex = insertIndex - 1;
          }
        }
      } else {
        const rootOrder = Array.from(this.rootIds);
        const oldIndex = rootOrder.indexOf(nodeId);
        if (oldIndex >= 0 && insertIndex > oldIndex) {
          adjustedInsertIndex = insertIndex - 1;
        }
      }
    }

    // Remove from old parent
    if (node.parentId) {
      const oldParent = this.nodes.get(node.parentId);
      if (oldParent) {
        oldParent.childrenIds = oldParent.childrenIds.filter(id => id !== nodeId);
      }
    } else {
      this.rootIds.delete(nodeId);
    }

    // Add to new parent
    if (newParentId) {
      const newParent = this.nodes.get(newParentId);
      if (newParent) {
        node.parentId = newParentId;
        newParent.childrenIds = newParent.childrenIds.filter(id => id !== nodeId);
        const idxRaw = adjustedInsertIndex == null ? newParent.childrenIds.length : adjustedInsertIndex;
        const idx = Math.max(0, Math.min(newParent.childrenIds.length, idxRaw));
        newParent.childrenIds.splice(idx, 0, nodeId);
      } else {
        node.parentId = null;
        this.rootIds.add(nodeId);
      }
    } else {
      node.parentId = null;
      if (adjustedInsertIndex == null) {
        this.rootIds.add(nodeId);
      } else {
        const rootOrder = Array.from(this.rootIds).filter(id => id !== nodeId);
        const idx = Math.max(0, Math.min(rootOrder.length, adjustedInsertIndex));
        rootOrder.splice(idx, 0, nodeId);
        this.rootIds = new Set(rootOrder);
      }
    }
  }

  /** Check if ancestorId is an ancestor of descendantId. */
  isDescendant(ancestorId: string, descendantId: string): boolean {
    let current = this.nodes.get(descendantId);
    while (current) {
      if (current.parentId === ancestorId) return true;
      current = current.parentId ? this.nodes.get(current.parentId) : undefined;
    }
    return false;
  }

  // === GROUPING ===

  /** Group multiple nodes under a new parent. */
  groupNodes(nodeIds: string[]): LayoutNode | null {
    if (nodeIds.length === 0) return null;

    const nodes = nodeIds
      .map(id => this.nodes.get(id))
      .filter((n): n is LayoutNode => n !== undefined);

    if (nodes.length === 0) return null;

    const group = createLayoutNode();
    const commonParentId = nodes[0].parentId;
    const bounds = this.computeGroupBounds(nodes);
    group.layoutBox.size = bounds.size;
    group.position = bounds.position;

    this.addNode(group, commonParentId ?? undefined);

    for (const node of nodes) {
      node.position = {
        x: node.resolvedPosition.x - bounds.position.x,
        y: node.resolvedPosition.y - bounds.position.y,
        z: node.resolvedPosition.z - bounds.position.z
      };
      this.reparent(node.id, group.id);
    }

    return group;
  }

  /** Ungroup a node, reparenting its children to the group's parent. */
  ungroup(groupId: string): void {
    const group = this.nodes.get(groupId);
    if (!group || group.childrenIds.length === 0) return;

    const parentId = group.parentId;

    for (const childId of [...group.childrenIds]) {
      const child = this.nodes.get(childId);
      if (child) {
        child.position = {
          x: child.resolvedPosition.x + group.resolvedPosition.x,
          y: child.resolvedPosition.y + group.resolvedPosition.y,
          z: child.resolvedPosition.z + group.resolvedPosition.z
        };
        this.reparent(childId, parentId);
      }
    }

    this.removeNode(groupId);
  }

  /** Convert a group to an auto layout container. */
  convertToAutoLayout(nodeId: string): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;

    node.layoutContainer = createLayoutContainer();

    for (const childId of node.childrenIds) {
      const child = this.nodes.get(childId);
      // Unset, not seeded — see addNode: the engine inherits at solve time.
      if (child && !child.layoutItem) {
        child.layoutItem = createLayoutItem();
      }
    }
  }

  /** Remove auto layout from a container. */
  removeAutoLayout(nodeId: string): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;

    node.layoutContainer = null;

    for (const childId of node.childrenIds) {
      const child = this.nodes.get(childId);
      if (child) {
        child.layoutItem = null;
      }
    }
  }

  // === BOUNDS COMPUTATION ===

  private computeGroupBounds(nodes: LayoutNode[]): { position: Vector3D; size: Vector3D } {
    if (nodes.length === 0) {
      return { position: createVector3D(), size: createVector3D(100, 100, 100) };
    }

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    for (const node of nodes) {
      const pos = node.resolvedPosition;
      const size = node.resolvedSize;
      minX = Math.min(minX, pos.x);
      minY = Math.min(minY, pos.y);
      minZ = Math.min(minZ, pos.z);
      maxX = Math.max(maxX, pos.x + size.x);
      maxY = Math.max(maxY, pos.y + size.y);
      maxZ = Math.max(maxZ, pos.z + size.z);
    }

    return {
      position: { x: minX, y: minY, z: minZ },
      size: { x: maxX - minX, y: maxY - minY, z: maxZ - minZ }
    };
  }

  // === TRAVERSAL ===

  /** Traverse tree depth-first. */
  traverse(callback: (node: LayoutNode, depth: number) => void): void {
    const visit = (nodeId: string, depth: number) => {
      const node = this.nodes.get(nodeId);
      if (!node) return;
      callback(node, depth);
      for (const childId of node.childrenIds) {
        visit(childId, depth + 1);
      }
    };
    for (const rootId of this.rootIds) {
      visit(rootId, 0);
    }
  }

  /** Traverse tree bottom-up (children before parents). */
  traverseBottomUp(callback: (node: LayoutNode) => void): void {
    const visit = (nodeId: string) => {
      const node = this.nodes.get(nodeId);
      if (!node) return;
      for (const childId of node.childrenIds) {
        visit(childId);
      }
      callback(node);
    };
    for (const rootId of this.rootIds) {
      visit(rootId);
    }
  }

  // === SERIALIZATION ===

  /** Export tree to JSON-serializable format. */
  toJSON(): LayoutNode[] {
    return Array.from(this.nodes.values());
  }

  /** Import tree from JSON. */
  fromJSON(nodes: LayoutNode[]): void {
    this.nodes.clear();
    this.rootIds.clear();

    for (const node of nodes) {
      this.nodes.set(node.id, node);
    }
    for (const node of nodes) {
      if (!node.parentId) {
        this.rootIds.add(node.id);
      }
    }
  }
}
